import {
  browserSupportsWebAuthn,
  startAuthentication,
} from '@simplewebauthn/browser';
import { apiRequest } from 'platform/utilities/api';
import environment from 'platform/utilities/environment';
import recordEvent from 'platform/monitoring/record-event';
import {
  API_SIGN_IN_SERVICE_URL,
  CLIENT_IDS,
  COOKIES,
  OAUTH_ALLOWED_PARAMS,
  OAUTH_KEYS,
} from 'platform/utilities/oauth/constants';
import { getOAuthRequestParams } from 'platform/utilities/oauth/utilities';
import { AUTHN_SETTINGS, CSP_IDS } from './constants';
import { externalApplicationsConfig } from './usip-config';

const PASSKEY_API_URL = environment.API_URL;
const PASSKEY_AUTHENTICATION_OPTIONS_URL = `${PASSKEY_API_URL}/sign_in/webauthn/authentications/options`;
const PASSKEY_AUTHENTICATION_VERIFY_URL = `${PASSKEY_API_URL}/sign_in/webauthn/authentications/verify`;
const PASSKEY_AUTHENTICATION_STATUS_URL = `${PASSKEY_API_URL}/sign_in/webauthn/authentications/status`;

const PASSKEY_ACR = 'urn:acr.va.gov:verified-facial-match-preferred';

const PASSKEY_START_ERROR =
  'Unable to start your passkey sign in. Please try again or use another sign in option.';
const PASSKEY_OPTIONS_ERROR = 'Unable to get your passkey sign in options.';
const PASSKEY_VERIFY_ERROR =
  'Authentication failed. Please try again or use an alternative sign in method.';

const jsonPostRequest = body => ({
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Key-Inflection': null,
  },
  body: JSON.stringify(body),
});

const getPasskeyOAuthParams = async ({
  application,
  clientId,
  queryParams,
}) => {
  try {
    return await getOAuthRequestParams({
      application,
      clientId,
      type: CSP_IDS.WEBAUTHN,
      passedQueryParams: queryParams,
      acr: PASSKEY_ACR,
    });
  } catch {
    throw new Error(PASSKEY_START_ERROR);
  }
};

const buildAuthorizeBody = ({ oAuthParams, queryParams }) => ({
  type: CSP_IDS.WEBAUTHN,
  [OAUTH_KEYS.CLIENT_ID]: oAuthParams.usedClientId,
  [OAUTH_KEYS.ACR]: oAuthParams.usedAcr,
  [OAUTH_KEYS.CODE_CHALLENGE]: oAuthParams.codeChallenge,
  [OAUTH_KEYS.CODE_CHALLENGE_METHOD]: OAUTH_ALLOWED_PARAMS.S256,
  ...(oAuthParams.usedState && { [OAUTH_KEYS.STATE]: oAuthParams.usedState }),
  ...(oAuthParams.isMobileOAuth &&
    queryParams.scope && { [OAUTH_ALLOWED_PARAMS.SCOPE]: queryParams.scope }),
  ...(queryParams.authorizeSsoId && {
    [OAUTH_KEYS.AUTHORIZE_SSO_ID]: queryParams.authorizeSsoId,
  }),
});

const requestPasskeyAuthorization = async authorizeBody => {
  let data;

  try {
    data = await apiRequest(
      API_SIGN_IN_SERVICE_URL({}),
      jsonPostRequest(authorizeBody),
    );
  } catch {
    throw new Error(PASSKEY_START_ERROR);
  }

  if (!data?.state) {
    throw new Error(PASSKEY_START_ERROR);
  }

  return data.state;
};

const getPasskeyAuthenticationOptions = async signedState => {
  let data;

  try {
    data = await apiRequest(
      PASSKEY_AUTHENTICATION_OPTIONS_URL,
      jsonPostRequest({ state: signedState }),
    );
  } catch {
    throw new Error(PASSKEY_OPTIONS_ERROR);
  }

  if (!data?.options) {
    throw new Error(PASSKEY_OPTIONS_ERROR);
  }

  return data.options;
};

export async function requestPasskeySignInState({
  application,
  clientId,
  queryParams = {},
} = {}) {
  const oAuthParams = await getPasskeyOAuthParams({
    application,
    clientId,
    queryParams,
  });

  recordEvent({
    event: `login-attempted-${CSP_IDS.WEBAUTHN}-oauth-${oAuthParams.usedClientId}`,
  });

  const signedState = await requestPasskeyAuthorization(
    buildAuthorizeBody({ oAuthParams, queryParams }),
  );

  return { oAuthParams, signedState };
}

export const storePasskeyOriginClient = ({
  application,
  clientId,
  codeChallenge,
  state,
  scope,
  authorizeSsoId,
} = {}) => {
  const originClientId =
    clientId || externalApplicationsConfig[application]?.oAuthOptions?.clientId;

  if (!originClientId || originClientId === CLIENT_IDS.VAWEB) {
    sessionStorage.removeItem(AUTHN_SETTINGS.PASSKEY_ORIGIN_CLIENT);
    return;
  }

  sessionStorage.setItem(
    AUTHN_SETTINGS.PASSKEY_ORIGIN_CLIENT,
    JSON.stringify({
      application,
      clientId: originClientId,
      codeChallenge,
      state,
      scope,
      authorizeSsoId,
    }),
  );
};

export const clearPasskeyOriginClient = () =>
  sessionStorage.removeItem(AUTHN_SETTINGS.PASSKEY_ORIGIN_CLIENT);

export const getPasskeyOriginClient = () => {
  try {
    const originClient = JSON.parse(
      sessionStorage.getItem(AUTHN_SETTINGS.PASSKEY_ORIGIN_CLIENT),
    );
    return originClient?.clientId ? originClient : null;
  } catch {
    return null;
  }
};

export const reportPasskeyAuthenticationStatus = (
  signedState,
  status,
  errorName,
) => {
  const body = { state: signedState, status };
  if (errorName) body.error = errorName;
  apiRequest(PASSKEY_AUTHENTICATION_STATUS_URL, {
    ...jsonPostRequest(body),
    keepalive: true,
  }).catch(() => {});
};

export const getPasskeyErrorMessage = error => {
  if (error?.name === 'NotAllowedError') {
    return 'Passkey sign in was canceled. You can try again or use another sign in option.';
  }

  return (
    error?.message ||
    'Unable to authenticate with your passkey. Please try again.'
  );
};

export async function authenticateWithPasskey({
  application,
  clientId,
  queryParams = {},
  onOptionsSuccess,
} = {}) {
  if (!browserSupportsWebAuthn()) {
    throw new Error('Passkeys are not supported on this browser.');
  }

  const { oAuthParams, signedState } = await requestPasskeySignInState({
    application,
    clientId,
    queryParams,
  });

  const options = await getPasskeyAuthenticationOptions(signedState);

  onOptionsSuccess?.(signedState);

  let attest;
  try {
    attest = await startAuthentication({
      optionsJSON: options,
      useBrowserAutofill: false,
    });
  } catch (error) {
    reportPasskeyAuthenticationStatus(signedState, 'failed', error.name);
    error.signedState = signedState;
    throw error;
  }

  const verifyJson = await apiRequest(
    PASSKEY_AUTHENTICATION_VERIFY_URL,
    jsonPostRequest({ authentication: { attest }, state: signedState }),
  );

  if (!verifyJson?.redirect_url) {
    throw new Error(PASSKEY_VERIFY_ERROR);
  }

  sessionStorage.setItem(COOKIES.CI, oAuthParams.usedClientId);

  return { redirectUrl: verifyJson.redirect_url };
}
